import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  IBaseUser,
  IUser,
  IUserBankDetails,
  IUserNotifications,
  IUserTutorDetails,
  IUserOneToOneTutorRate,
  IUserGroupTutorRate,
} from '.';
import ApiDataProviderV2, {
  TApiPaginationResponse,
  TApiResponse,
} from '../../api/ApiDataProvider';
import { AdminApiPaths as AdminApiPathsV2 } from '../../api/APIPaths';

export type TGetUserListByNameAsAdmin = {
  offset: number;
  limit: number;
  name?: string;
  country?: string;
  level?: string;
  userTypes?: Array<'student' | 'tutor' | 'admin' | 'guest'>;
};

export type TAdminUserListGet = {
  limit: number;
  offset: number;
  sortKey?: string;
  sortValue?: number;
  searchOption?: string;
  searchText?: string;
  role?: string;
  country?: string;
  group?: string;
};

export type TCreateUserAsAdmin = {
  name: string;
  email: string;
  phoneNumber: string;
  country: string;
  role: string;
};

export type TUpdateUserAsAdmin = {
  _id: string;
  firstname?: string;
  lastname?: string;
  gender?: string;
  dateOfBirth?: string;
  email?: string;
  billingEmail?: string;
  phoneNumber?: string;
  country?: string;
  avatar?: string;
  examDate?: string;
  schoolName?: string;
  parentName?: string;
  parentEmail?: string;
  parentPhoneNumber?: string;
  nationality?: string;
  emergencyNumber?: string;
  notifications?: IUserNotifications;
  tutorDetails?: IUserTutorDetails;
  bankDetails?: IUserBankDetails;
  groups?: string[];
  adminTag?: string;
  roleId?: string;
  msTeamsEmail?: string;
};

export type TUpdateUserPlanDataAsAdmin = {
  id: string;
  curriculum?: string;
  grade?: string;
  oneToOneStudentType?: string;
  premiumExpirationDate?: string;
  flexibleRates: Array<{
    planId: string;
    amount: number;
    currency: string;
    country?: string;
    curriculum?: string;
    grade?: string;
  }>;
  subscriptionRates: Array<{
    planId: string;
    amount: number;
    currency: string;
    country?: string;
    curriculum?: string;
    grade?: string;
  }>;
};

export type TAdminUserRecordDelete = {
  _id?: string;
  userId: string;
};

export type TAdminUserExperience = TAdminUserRecordDelete & {
  jobTitle: string;
  workPlace: string;
  startDate: string;
  endDate: string;
};

export type TAdminUserQualification = TAdminUserRecordDelete & {
  qualification: string;
  institute: string;
  startDate: string;
  endDate: string;
};

export type TAdminUserAchievement = TAdminUserRecordDelete & {
  awardTitle: string;
  awardBody: string;
  awardDate: string;
};

export type TAdminUserCertification = TAdminUserRecordDelete & {
  certificationTitle: string;
  certificationBody: string;
  certificationDate: string;
};

export type TAdminUserLanguage = TAdminUserRecordDelete & {
  language: string;
  proficiency: string;
};

export type TAdminUserSubject = TAdminUserRecordDelete & {
  subject: string;
  curriculum: string;
  grade: string;
};

export type TAdminCreateUserAdminNote = {
  id: string;
  description: string;
};

export type TAdminDeleteUserAdminNote = {
  id: string;
  noteId: string;
};

export type TAdminUpdateUserOneToOneTutorRates = {
  userId: string;
  oneToOneTutorRates?: IUserOneToOneTutorRate[];
};

export type TAdminUpdateUserGroupTutorRates = {
  userId: string;
  groupTutorRates?: IUserGroupTutorRate[];
};

export type TAdminResendTempUserPassword = {
  userId: string;
};

const apiDataProviderV2 = new ApiDataProviderV2();

// V2
export const getUserListForDropDownAsAdmin = createAsyncThunk(
  '/admin/users/list-for-dropdown',
  async ({
    limit,
    offset,
    name,
    country,
    level,
    userTypes,
  }: TGetUserListByNameAsAdmin) => {
    let path = `${AdminApiPathsV2.users}/dropdown?limit=${limit}&offset=${offset}`;

    if (name) {
      path += `&name=${name}`;
    }

    if (country) {
      path += `&country=${country}`;
    }

    if (level) {
      path += `&level=${level}`;
    }

    if (userTypes && userTypes.length) {
      path += `&userTypes=${userTypes.join(',')}`;
    }

    const response = await apiDataProviderV2.get<
      TApiResponse<TApiPaginationResponse<IBaseUser>>
    >(path);

    return response.data;
  }
);

export const getUserListAsAdmin = createAsyncThunk(
  '/admin/users/list',
  async ({
    limit,
    offset,
    sortKey,
    sortValue,
    searchOption,
    searchText,
    role,
    country,
    group,
  }: TAdminUserListGet) => {
    let path = `${AdminApiPathsV2.users}?limit=${limit}&offset=${offset}`;

    if (searchOption) {
      path += `&searchOption=${searchOption}`;
    }
    if (searchText) {
      path += `&searchText=${searchText}`;
    }
    if (role) {
      path += `&role=${role}`;
    }
    if (country) {
      path += `&country=${country}`;
    }
    if (group) {
      path += `&group=${group}`;
    }
    if (sortKey) {
      path += `&sortKey=${sortKey}`;
    }
    if (sortValue) {
      path += `&sortValue=${sortValue}`;
    }

    const response = await apiDataProviderV2.get<
      TApiResponse<TApiPaginationResponse<IUser>>
    >(path);

    return response.data;
  }
);

export const getUserByIdAsAdmin = createAsyncThunk(
  '/admin/users/get-by-id',
  async (id: string) => {
    const path = `${AdminApiPathsV2.users}/quiztopiya/${id}`;
    const response = await apiDataProviderV2.get<TApiResponse<IUser>>(path);

    return response.data;
  }
);

export const createUserAsAdmin = createAsyncThunk(
  '/admin/user/create',
  async (data: TCreateUserAsAdmin) => {
    const path = `${AdminApiPathsV2.users}`;
    const response = await apiDataProviderV2.post<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const updateUserAsAdmin = createAsyncThunk(
  '/admin/user/update',
  async ({ _id, ...data }: TUpdateUserAsAdmin) => {
    const path = `${AdminApiPathsV2.users}/${_id}`;
    const response = await apiDataProviderV2.put<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const addTutorExperienceAsAdmin = createAsyncThunk(
  '/admin/user/experience/add',
  async (data: TAdminUserExperience) => {
    const path = `${AdminApiPathsV2.users}/experiences`;
    const response = await apiDataProviderV2.post<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const updateTutorExperienceAsAdmin = createAsyncThunk(
  '/admin/user/experience/update',
  async ({ userId, ...data }: TAdminUserExperience) => {
    const path = `${AdminApiPathsV2.users}/experiences/${userId}`;
    const response = await apiDataProviderV2.put<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const deleteTutorExperienceAsAdmin = createAsyncThunk(
  '/admin/user/experience/delete',
  async ({ _id, userId }: TAdminUserRecordDelete) => {
    const path = `${AdminApiPathsV2.users}/experiences/${_id}?userId=${userId}`;
    const response = await apiDataProviderV2.delete<TApiResponse<IUser>>(path);

    return response.data;
  }
);

export const addTutorQualificationAsAdmin = createAsyncThunk(
  '/admin/user/qualifications/add',
  async (data: TAdminUserQualification) => {
    const path = `${AdminApiPathsV2.users}/qualifications`;
    const response = await apiDataProviderV2.post<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const updateTutorQualificationAsAdmin = createAsyncThunk(
  '/admin/user/qualifications/update',
  async ({ userId, ...data }: TAdminUserQualification) => {
    const path = `${AdminApiPathsV2.users}/qualifications/${userId}`;
    const response = await apiDataProviderV2.put<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const deleteTutorQualificationAsAdmin = createAsyncThunk(
  '/admin/user/qualifications/delete',
  async ({ _id, userId }: TAdminUserRecordDelete) => {
    const path = `${AdminApiPathsV2.users}/qualifications/${_id}?userId=${userId}`;
    const response = await apiDataProviderV2.delete<TApiResponse<IUser>>(path);

    return response.data;
  }
);

export const addTutorAchievementAsAdmin = createAsyncThunk(
  '/admin/user/achievements/add',
  async (data: TAdminUserAchievement) => {
    const path = `${AdminApiPathsV2.users}/achievements`;
    const response = await apiDataProviderV2.post<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const updateTutorAchievementAsAdmin = createAsyncThunk(
  '/admin/user/achievements/update',
  async ({ userId, ...data }: TAdminUserAchievement) => {
    const path = `${AdminApiPathsV2.users}/achievements/${userId}`;
    const response = await apiDataProviderV2.put<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const deleteTutorAchievementAsAdmin = createAsyncThunk(
  '/admin/user/achievements/delete',
  async ({ _id, userId }: TAdminUserRecordDelete) => {
    const path = `${AdminApiPathsV2.users}/achievements/${_id}?userId=${userId}`;
    const response = await apiDataProviderV2.delete<TApiResponse<IUser>>(path);

    return response.data;
  }
);

export const addTutorCertificationAsAdmin = createAsyncThunk(
  '/admin/user/certifications/add',
  async (data: TAdminUserCertification) => {
    const path = `${AdminApiPathsV2.users}/certifications`;
    const response = await apiDataProviderV2.post<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const updateTutorCertificationAsAdmin = createAsyncThunk(
  '/admin/user/certifications/update',
  async ({ userId, ...data }: TAdminUserCertification) => {
    const path = `${AdminApiPathsV2.users}/certifications/${userId}`;
    const response = await apiDataProviderV2.put<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const deleteTutorCertificationAsAdmin = createAsyncThunk(
  '/admin/user/certifications/delete',
  async ({ _id, userId }: TAdminUserRecordDelete) => {
    const path = `${AdminApiPathsV2.users}/certifications/${_id}?userId=${userId}`;
    const response = await apiDataProviderV2.delete<TApiResponse<IUser>>(path);

    return response.data;
  }
);

export const addTutorLanguageAsAdmin = createAsyncThunk(
  '/admin/user/languages/add',
  async (data: TAdminUserLanguage) => {
    const path = `${AdminApiPathsV2.users}/languages`;
    const response = await apiDataProviderV2.post<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const updateTutorLanguageAsAdmin = createAsyncThunk(
  '/admin/user/languages/update',
  async ({ userId, ...data }: TAdminUserLanguage) => {
    const path = `${AdminApiPathsV2.users}/languages/${userId}`;
    const response = await apiDataProviderV2.put<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const deleteTutorLanguageAsAdmin = createAsyncThunk(
  '/admin/user/languages/delete',
  async ({ _id, userId }: TAdminUserRecordDelete) => {
    const path = `${AdminApiPathsV2.users}/languages/${_id}?userId=${userId}`;
    const response = await apiDataProviderV2.delete<TApiResponse<IUser>>(path);

    return response.data;
  }
);

export const addUserSubjectAsAdmin = createAsyncThunk(
  '/admin/user/subjects/add',
  async (data: TAdminUserSubject) => {
    const path = `${AdminApiPathsV2.users}/subjects`;
    const response = await apiDataProviderV2.post<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const deleteUserSubjectAsAdmin = createAsyncThunk(
  '/admin/user/subjects/delete',
  async ({ _id, userId }: TAdminUserRecordDelete) => {
    const path = `${AdminApiPathsV2.users}/subjects/${_id}?userId=${userId}`;
    const response = await apiDataProviderV2.delete<TApiResponse<IUser>>(path);

    return response.data;
  }
);

export const updateUserPlanDataAsAdmin = createAsyncThunk(
  '/admin/user/flexible-plans/update',
  async (data: TUpdateUserPlanDataAsAdmin) => {
    const path = `${AdminApiPathsV2.users}/plans/${data.id}`;
    const response = await apiDataProviderV2.put<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const adminResendUserTempPassword = createAsyncThunk(
  '/admin/user/resend-user-password/id',
  async (data: TAdminResendTempUserPassword) => {
    const path = `${AdminApiPathsV2.users}/resend-user-password/${data.userId}?userId=${data.userId}`;
    const response = await apiDataProviderV2.post<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

// ADMIN NOTES
export const adminCreateUserAdminNote = createAsyncThunk(
  '/admin/user/admin-notes/create',
  async (data: TAdminCreateUserAdminNote) => {
    const response = await apiDataProviderV2.post<TApiResponse<IUser>>(
      `${AdminApiPathsV2.users}/admin-notes/${data.id}`,
      data
    );

    return response.data;
  }
);

export const adminDeleteUserAdminNote = createAsyncThunk(
  '/admin/user/admin-notes/delete',
  async ({ id, noteId }: TAdminDeleteUserAdminNote) => {
    const response = await apiDataProviderV2.delete<TApiResponse<IUser>>(
      `${AdminApiPathsV2.users}/admin-notes/${id}?id=${id}&noteId=${noteId}`
    );

    return response.data;
  }
);

export const updateUserOneToOneTutorRatesAsAdmin = createAsyncThunk(
  '/admin/user/one-to-one-tutor-rates/update',
  async (data: TAdminUpdateUserOneToOneTutorRates) => {
    const path = `${AdminApiPathsV2.users}/one-to-one-tutor-rates/${data.userId}`;

    const response = await apiDataProviderV2.put<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const updateUserGroupTutorRatesAsAdmin = createAsyncThunk(
  '/admin/user/group-tutor-rates/update',
  async (data: TAdminUpdateUserGroupTutorRates) => {
    const path = `${AdminApiPathsV2.users}/group-tutor-rates/${data.userId}`;

    const response = await apiDataProviderV2.put<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);
